import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import { type ContractDto } from '@b-stock/contract-api-client'
import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'
import { OfferingDtoOfferAmountUnitsEnum } from '@b-stock/offering-api-client'

const ns = 'Offerings.Details.form.errors'

export const useValidationSchema = ({
  bidPrecision,
  contract,
  offerAmountUnits,
}: {
  bidPrecision: BidPrecision
  contract?: ContractDto | null
  offerAmountUnits: OfferingDtoOfferAmountUnitsEnum
}) => {
  const { formatMessage } = useIntl()

  const isContract = !!(contract && contract.bidBasis)
  const isAbsolute = isContract
    ? contract.bidBasis === 'ABSOLUTE'
    : offerAmountUnits === 'PRICE'

  const { formatPrecisionNumber: formatNumberWithPrecision } =
    usePrecisionNumberFormat({
      precision: bidPrecision,
      format: isAbsolute ? 'currency' : 'percent',
      currency: 'USD',
    })

  return useMemo(
    () =>
      Yup.object()
        .shape({
          isWinnerTakeAll: Yup.boolean().required(),
          itemRows: Yup.array()
            .of(
              Yup.object().shape({
                isWinnerTakeAll: Yup.boolean().required(),
                item: Yup.object().shape({
                  quantity: Yup.number().min(0).required(),
                }),
                offerAmount: Yup.number().nullable(),
                offerAmountUnits: Yup.string().oneOf(
                  Object.values(OfferingDtoOfferAmountUnitsEnum)
                ),
                offerQuantity: Yup.number().integer().min(0).nullable(),
                offerStartPrice: Yup.number().nullable(),
              })
            )
            .min(1),
        })
        .test({
          test: (
            { isWinnerTakeAll, itemRows },
            ctx
          ): Yup.ValidationError | boolean => {
            const errors: Yup.ValidationError[] = []

            const storedPrecision = isAbsolute ? 0.01 : 1
            let precision = storedPrecision * bidPrecision
            if (isAbsolute && !isContract) {
              // No fractions of a cent for non-unit amount
              precision = Math.max(precision, 0.01)
            }

            itemRows?.forEach((row, index) => {
              const isRowRequired = isWinnerTakeAll || itemRows.length === 1

              if (typeof row.offerAmount === 'number') {
                if (row.offerAmount < precision) {
                  errors.push(
                    ctx.createError({
                      message: formatMessage(
                        { id: `${ns}.offerAmountTooLow` },
                        {
                          min: formatNumberWithPrecision(bidPrecision),
                        }
                      ),
                      path: `itemRows.${index}.offerAmount`,
                    })
                  )
                }

                if (!isAbsolute && row.offerAmount > 100) {
                  errors.push(
                    ctx.createError({
                      message: `${ns}.offerAmountTooHigh_RELATIVE`,
                      path: `itemRows.${index}.offerAmount`,
                    })
                  )
                }

                if (
                  typeof row.offerStartPrice === 'number' &&
                  row.offerAmount < row.offerStartPrice
                ) {
                  errors.push(
                    ctx.createError({
                      message: formatMessage(
                        { id: `${ns}.offerAmountTooLow` },
                        {
                          min: formatNumberWithPrecision(row.offerStartPrice),
                        }
                      ),
                      path: `itemRows.${index}.offerAmount`,
                    })
                  )
                }
              }

              if (!row.offerAmount && (row.offerQuantity || isRowRequired)) {
                errors.push(
                  ctx.createError({
                    message: `${ns}.offerAmountRequired`,
                    path: `itemRows.${index}.offerAmount`,
                  })
                )
              }

              if (
                !row.offerQuantity &&
                !isContract && // contract items have no quantity
                (row.offerAmount || isRowRequired)
              ) {
                errors.push(
                  ctx.createError({
                    message: `${ns}.offerQuantityRequired`,
                    path: `itemRows.${index}.offerQuantity`,
                  })
                )
              }

              if (row.offerQuantity && row.offerQuantity > row.item.quantity) {
                errors.push(
                  ctx.createError({
                    message: `${ns}.offerQuantityTooHigh`,
                    path: `itemRows.${index}.offerQuantity`,
                  })
                )
              }
            })
            return errors.length ? new Yup.ValidationError(errors) : true
          },
        }),
    [
      bidPrecision,
      formatMessage,
      formatNumberWithPrecision,
      isContract,
      isAbsolute,
    ]
  )
}
